import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  ArrowsRightLeftIcon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, SortableTableHeader, Text } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { useAuthContext } from '../../contexts/AuthContext'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { estoqueService } from '../../services/estoque'
import { marketingSolicitacaoService } from '../../services/marketingSolicitacao'
import { supplierQuotesService } from '../../services/supplierQuotes'
import type { EstoqueFornecedor } from '../../types/estoque'
import type { MarketingRequest } from '../../types/marketingSolicitacao'
import type {
  SupplierQuote,
  SupplierQuoteInput,
  SupplierQuoteStatus,
} from '../../types/supplierQuotes'
import { paginateRows, sortRows } from '../Estoque/estoqueUi'
import EstoquePagination from '../Estoque/EstoquePagination'
import PropostasCompareModal from './PropostasCompareModal'
import PropostasFormModal from './PropostasFormModal'
import SolicitacaoPickerField from './SolicitacaoPickerField'
import {
  ICON_BTN_CLASS,
  ICON_BTN_DANGER_CLASS,
  MODULE_NAME,
  PAGE_SIZE,
  PERM_LOADING,
  SELECT_CLASS,
  STATUS_LABELS,
  formatCurrency,
  formatDateOnlyPtBr,
  formatQuoteNumber,
  quoteTotal,
  statusBadgeClass,
  statusLabel,
} from './propostasUi'
import { formatRequestNumber } from './solicitacaoUi'

type Filters = {
  search: string
  status: SupplierQuoteStatus | ''
  supplierId: string
  requestId: string
  dateFrom: string
  dateTo: string
}

const DEFAULT_FILTERS: Filters = {
  search: '',
  status: '',
  supplierId: '',
  requestId: '',
  dateFrom: '',
  dateTo: '',
}

function quoteLogSnapshot(quote: SupplierQuote) {
  return {
    quote_number: quote.quote_number,
    title: quote.title,
    status: quote.status,
    supplier_id: quote.supplier_id,
    supplier_name: quote.supplier?.name ?? null,
    request_id: quote.request_id,
    request_number: quote.request?.request_number ?? null,
    freight_value: quote.freight_value,
    discount_value: quote.discount_value,
    valid_until: quote.valid_until,
    delivery_deadline: quote.delivery_deadline,
    items_count: quote.items?.length || 0,
    total: quoteTotal(quote),
    invoice_id: quote.invoice?.id ?? null,
    invoice_number: quote.invoice?.number ?? null,
    created_by: quote.created_by,
    created_by_name: quote.created_by_user?.name ?? null,
    deleted_by: quote.deleted_by,
    deleted_by_name: quote.deleted_by_user?.name ?? null,
  }
}

const MarketingPropostas: React.FC = () => {
  const [searchParams] = useSearchParams()
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete, logView } = useUserLogs()
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')
  const { hasPermission: canViewValues, loading: viewValuesLoading } = usePermission(
    MODULE_NAME,
    'view_values'
  )
  const showValues = viewValuesLoading || canViewValues

  const [quotes, setQuotes] = useState<SupplierQuote[]>([])
  const [fornecedores, setFornecedores] = useState<EstoqueFornecedor[]>([])
  const [requests, setRequests] = useState<MarketingRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<Filters>(() => ({
    ...DEFAULT_FILTERS,
    search: searchParams.get('q')?.trim() || '',
  }))

  const [sortKey, setSortKey] = useState('number')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<SupplierQuote | null>(null)
  const [viewing, setViewing] = useState<SupplierQuote | null>(null)
  const [saving, setSaving] = useState(false)
  const [compareQuotes, setCompareQuotes] = useState<SupplierQuote[] | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [list, fornecedoresData, requestsData] = await Promise.all([
        supplierQuotesService.list(),
        estoqueService.listFornecedores(true),
        marketingSolicitacaoService.listRequests(),
      ])
      setQuotes(list)
      setFornecedores(fornecedoresData)
      setRequests(requestsData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar propostas.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    void logView('supplier_quote', null, 'Visualizou tela de propostas de fornecedor')
  }, [logView])

  const filtered = useMemo(() => {
    const term = filters.search.trim().toLowerCase()
    return quotes.filter((row) => {
      if (filters.status && row.status !== filters.status) return false
      if (filters.supplierId && row.supplier_id !== filters.supplierId) return false
      if (filters.requestId && row.request_id !== filters.requestId) return false
      const created = row.created_at?.slice(0, 10) || ''
      if (filters.dateFrom && (!created || created < filters.dateFrom)) return false
      if (filters.dateTo && (!created || created > filters.dateTo)) return false
      if (term) {
        const haystack = [
          String(row.quote_number),
          row.title,
          row.supplier?.name || '',
          row.request?.request_number != null ? String(row.request.request_number) : '',
        ]
          .join(' ')
          .toLowerCase()
        if (!haystack.includes(term)) return false
      }
      return true
    })
  }, [quotes, filters])

  const sorted = useMemo(
    () =>
      sortRows(filtered, sortKey, sortOrder, (row, key) => {
        if (key === 'number') return row.quote_number
        if (key === 'title') return row.title
        if (key === 'supplier') return row.supplier?.name || ''
        if (key === 'request') return row.request?.request_number || 0
        if (key === 'status') return row.status
        if (key === 'total') return quoteTotal(row)
        if (key === 'created') return row.created_at || ''
        return ''
      }),
    [filtered, sortKey, sortOrder]
  )

  const pagedRows = useMemo(() => paginateRows(sorted, page, PAGE_SIZE), [sorted, page])

  useEffect(() => {
    setPage(1)
  }, [filters, sortKey, sortOrder])

  const compareEligibleRequestIds = useMemo(() => {
    const counts = new Map<string, number>()
    for (const quote of quotes) {
      if (!quote.request_id) continue
      if (quote.status === 'descartada') continue
      counts.set(quote.request_id, (counts.get(quote.request_id) || 0) + 1)
    }
    return new Set(
      Array.from(counts.entries())
        .filter(([, count]) => count > 1)
        .map(([id]) => id)
    )
  }, [quotes])

  const openCreate = () => {
    if (!canInsert) return
    setEditing(null)
    setModalOpen(true)
  }

  const openEdit = (row: SupplierQuote) => {
    if (!canUpdate || row.status === 'nota_gerada') return
    setEditing(row)
    setModalOpen(true)
  }

  const openView = (row: SupplierQuote) => {
    setViewing(row)
    void logView(
      'supplier_quote',
      row.id,
      `Visualizou proposta ${formatQuoteNumber(row.quote_number)}: ${row.title}`
    )
  }

  const handleSave = async (input: SupplierQuoteInput) => {
    if (editing) {
      if (!canUpdate) return
    } else if (!canInsert) {
      return
    }
    setSaving(true)
    setError(null)
    try {
      if (editing) {
        const updated = await supplierQuotesService.update(editing.id, input)
        void logUpdate(
          'supplier_quote',
          updated.id,
          `Atualizou proposta ${formatQuoteNumber(updated.quote_number)}: ${updated.title}`,
          quoteLogSnapshot(editing),
          quoteLogSnapshot(updated)
        )
      } else {
        const created = await supplierQuotesService.create(input, user?.id)
        void logCreate(
          'supplier_quote',
          created.id,
          `Criou proposta ${formatQuoteNumber(created.quote_number)}: ${created.title}`,
          quoteLogSnapshot(created)
        )
      }
      setModalOpen(false)
      setEditing(null)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar proposta.')
      throw err
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (row: SupplierQuote) => {
    if (!canDelete || row.status === 'nota_gerada') return
    if (
      !window.confirm(
        `Excluir logicamente a proposta ${formatQuoteNumber(row.quote_number)}? O registro permanece no banco com quem excluiu.`
      )
    ) {
      return
    }
    try {
      await supplierQuotesService.softDelete(row.id, {
        deleted_by: user?.id ?? null,
        deleted_reason: 'Exclusão lógica pela tela de propostas',
      })
      void logDelete(
        'supplier_quote',
        row.id,
        `Excluiu proposta ${formatQuoteNumber(row.quote_number)}: ${row.title}`,
        quoteLogSnapshot(row)
      )
      await load()
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Erro ao excluir proposta.')
    }
  }

  const openCompare = (requestId: string) => {
    const group = quotes.filter(
      (q) => q.request_id === requestId && q.status !== 'descartada'
    )
    setCompareQuotes(group)
    const requestNumber = group[0]?.request?.request_number
    void logView(
      'supplier_quote',
      null,
      requestNumber != null
        ? `Comparou propostas da solicitação ${formatRequestNumber(requestNumber)}`
        : 'Comparou propostas de fornecedor'
    )
  }

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortOrder(key === 'number' || key === 'created' || key === 'total' ? 'desc' : 'asc')
    }
  }

  const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((f) => ({ ...f, [key]: value }))
    setPage(1)
  }

  const filtersActive = useMemo(
    () =>
      Boolean(
        filters.search ||
          filters.status ||
          filters.supplierId ||
          filters.requestId ||
          filters.dateFrom ||
          filters.dateTo
      ),
    [filters]
  )

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS)
    setPage(1)
  }

  return (
    <div className="flex h-full flex-col overflow-x-hidden overflow-y-auto">
      <PermissionGuard
        moduleName={MODULE_NAME}
        permissionType="show"
        loadingComponent={PERM_LOADING}
        fallback={
          <div className="flex flex-1 items-center justify-center px-4 py-12">
            <div className="text-center">
              <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
                Acesso negado
              </Heading>
              <Text color="muted" className="mt-2">
                Você não tem permissão para visualizar propostas de fornecedor.
              </Text>
            </div>
          </div>
        }
      >
        <div className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div>
              <Heading as="h1" size="2xl" className="text-gray-900 dark:text-white">
                Propostas
              </Heading>
              <Text color="muted" className="mt-1">
                Orçamentos de fornecedores para comparação e vínculo com notas fiscais.
              </Text>
            </div>
            {canInsert && (
              <PermissionGuard
                moduleName={MODULE_NAME}
                permissionType="insert"
                loadingComponent={PERM_LOADING}
                fallback={null}
              >
                <Button
                  type="button"
                  variant="solid"
                  onClick={openCreate}
                  className="inline-flex items-center gap-2"
                >
                  <PlusIcon className="h-5 w-5" />
                  Nova proposta
                </Button>
              </PermissionGuard>
            )}
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="mb-4 space-y-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              <div className="min-w-0">
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                  Busca
                </label>
                <Input
                  className="w-full"
                  value={filters.search}
                  onChange={(e) => updateFilter('search', e.target.value)}
                  placeholder="Número, título, fornecedor..."
                />
              </div>
              <div className="min-w-0">
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                  Status
                </label>
                <select
                  className={SELECT_CLASS + ' w-full'}
                  value={filters.status}
                  onChange={(e) =>
                    updateFilter('status', e.target.value as SupplierQuoteStatus | '')
                  }
                >
                  <option value="">Todos</option>
                  {(Object.keys(STATUS_LABELS) as SupplierQuoteStatus[]).map((key) => (
                    <option key={key} value={key}>
                      {STATUS_LABELS[key]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-0">
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                  Fornecedor
                </label>
                <select
                  className={SELECT_CLASS + ' w-full'}
                  value={filters.supplierId}
                  onChange={(e) => updateFilter('supplierId', e.target.value)}
                >
                  <option value="">Todos</option>
                  {fornecedores.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-0">
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                  De
                </label>
                <Input
                  type="date"
                  className="w-full min-w-0"
                  value={filters.dateFrom}
                  onChange={(e) => updateFilter('dateFrom', e.target.value)}
                />
              </div>
              <div className="min-w-0">
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                  Até
                </label>
                <Input
                  type="date"
                  className="w-full min-w-0"
                  value={filters.dateTo}
                  onChange={(e) => updateFilter('dateTo', e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="min-w-0 flex-1">
                <SolicitacaoPickerField
                  label="Solicitação"
                  requests={requests}
                  value={filters.requestId}
                  onChange={(requestId) => updateFilter('requestId', requestId)}
                  labelClassName="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400"
                  emptyLabel="Todas as solicitações"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={clearFilters}
                disabled={!filtersActive}
                className="shrink-0"
              >
                Limpar filtros
              </Button>
            </div>
          </div>

          {loading && quotes.length === 0 ? (
            <div className="flex justify-center py-20">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <SortableTableHeader
                        sortKey="number"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        className="px-4 py-3"
                      >
                        Nº
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="title"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        className="px-4 py-3"
                      >
                        Título
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="supplier"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        className="px-4 py-3"
                      >
                        Fornecedor
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="request"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        className="px-4 py-3"
                      >
                        Solicitação
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="status"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        className="px-4 py-3"
                      >
                        Status
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="total"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        className="px-4 py-3"
                      >
                        Total
                      </SortableTableHeader>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Nota
                      </th>
                      <SortableTableHeader
                        sortKey="created"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        className="px-4 py-3"
                      >
                        Criada em
                      </SortableTableHeader>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {pagedRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={9}
                          className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400"
                        >
                          Nenhuma proposta encontrada.
                        </td>
                      </tr>
                    ) : (
                      pagedRows.map((row) => {
                        const canCompare =
                          !!row.request_id && compareEligibleRequestIds.has(row.request_id)
                        return (
                          <tr
                            key={row.id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-900/30"
                          >
                            <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                              {formatQuoteNumber(row.quote_number)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                              {row.title}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                              {row.supplier?.name || '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                              {row.request?.request_number != null
                                ? formatRequestNumber(row.request.request_number)
                                : 'Avulsa'}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3">
                              <span
                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(row.status)}`}
                              >
                                {statusLabel(row.status)}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 dark:text-white">
                              {showValues ? formatCurrency(quoteTotal(row)) : '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                              {row.invoice?.number ? `NF ${row.invoice.number}` : '—'}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                              {formatDateOnlyPtBr(row.created_at)}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-right">
                              <div className="inline-flex items-center gap-1">
                                {canCompare && (
                                  <button
                                    type="button"
                                    className={ICON_BTN_CLASS}
                                    title="Comparar propostas da solicitação"
                                    onClick={() => openCompare(row.request_id!)}
                                  >
                                    <ArrowsRightLeftIcon className="h-4 w-4" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className={ICON_BTN_CLASS}
                                  title="Visualizar"
                                  onClick={() => openView(row)}
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </button>
                                {canUpdate && row.status !== 'nota_gerada' && (
                                  <PermissionGuard
                                    moduleName={MODULE_NAME}
                                    permissionType="update"
                                    loadingComponent={PERM_LOADING}
                                    fallback={null}
                                  >
                                    <button
                                      type="button"
                                      className={ICON_BTN_CLASS}
                                      title="Editar"
                                      onClick={() => openEdit(row)}
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </button>
                                  </PermissionGuard>
                                )}
                                {canDelete && row.status !== 'nota_gerada' && (
                                  <PermissionGuard
                                    moduleName={MODULE_NAME}
                                    permissionType="delete"
                                    loadingComponent={PERM_LOADING}
                                    fallback={null}
                                  >
                                    <button
                                      type="button"
                                      className={ICON_BTN_DANGER_CLASS}
                                      title="Excluir"
                                      onClick={() => void handleDelete(row)}
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </PermissionGuard>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <div className="px-4 pb-3">
                <EstoquePagination
                  page={page}
                  pageSize={PAGE_SIZE}
                  total={sorted.length}
                  onPageChange={setPage}
                />
              </div>
            </div>
          )}
        </div>

        <PropostasFormModal
          open={modalOpen}
          editing={editing}
          busy={saving}
          onClose={() => {
            if (saving) return
            setModalOpen(false)
            setEditing(null)
          }}
          onSave={handleSave}
        />

        <PropostasCompareModal
          open={Boolean(compareQuotes)}
          quotes={compareQuotes || []}
          showValues={showValues}
          onClose={() => setCompareQuotes(null)}
        />

        {viewing && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={() => setViewing(null)} />
            <div className="relative z-10 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800">
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <Heading as="h3" size="xl">
                    {formatQuoteNumber(viewing.quote_number)} · {viewing.title}
                  </Heading>
                  <Text size="sm" color="muted" className="mt-1">
                    {viewing.supplier?.name || '—'} · {statusLabel(viewing.status)}
                  </Text>
                </div>
                <Button type="button" variant="outline" onClick={() => setViewing(null)}>
                  Fechar
                </Button>
              </div>
              <div className="space-y-2 text-sm">
                <Text>
                  Solicitação:{' '}
                  {viewing.request?.request_number != null
                    ? formatRequestNumber(viewing.request.request_number)
                    : 'Avulsa'}
                </Text>
                <Text>
                  Total: {showValues ? formatCurrency(quoteTotal(viewing)) : '—'}
                </Text>
                <Text>
                  Nota:{' '}
                  {viewing.invoice?.number
                    ? `${viewing.invoice.number} (${viewing.invoice.status})`
                    : '—'}
                </Text>
                <Text>
                  Cadastrado por:{' '}
                  {viewing.created_by_user?.name?.trim() || '—'}
                  {viewing.created_at
                    ? ` em ${formatDateOnlyPtBr(viewing.created_at)}`
                    : ''}
                </Text>
                {viewing.deleted_at ? (
                  <Text>
                    Excluído por:{' '}
                    {viewing.deleted_by_user?.name?.trim() || '—'}
                    {` em ${formatDateOnlyPtBr(viewing.deleted_at)}`}
                    {viewing.deleted_reason ? ` · ${viewing.deleted_reason}` : ''}
                  </Text>
                ) : null}
                <div className="mt-3 space-y-1">
                  {(viewing.items || []).map((item) => (
                    <div
                      key={item.id}
                      className="flex justify-between gap-2 rounded border border-gray-200 px-3 py-2 dark:border-gray-700"
                    >
                      <span>
                        {item.description || item.stock_item?.name || 'Item'} · {item.quantity}
                      </span>
                      <span>
                        {showValues
                          ? formatCurrency(Number(item.quantity) * Number(item.unit_value))
                          : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </PermissionGuard>
    </div>
  )
}

export default MarketingPropostas
